import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, Link, useNavigate, useLocation } from 'react-router-dom';
import type { Project } from '../services/projectService';
import { projectService } from '../services/projectService';
import type { SubProject } from '../services/subProjectService';
import { subProjectService, type CreateSubProjectRequest } from '../services/subProjectService';
import type { Solution as SolutionModel } from '../services/subProjectService';
import { solutionService } from '../services/solutionService';
import { knowledgeBaseService, type KnowledgeBase } from '../services/knowledgeBaseService';
import { searchService } from '../services/searchService';
import { useTranslation } from 'react-i18next';
import { useCanEditProjects, useCanDeleteProject, useCanCreateSubprojects, useCanPrepareRag, useCanViewDocuments, useCanDeleteKnowledgeBases } from '../hooks/usePermissions';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/ToastProvider';
import SpeakingChatbotV2Assistant from '../components/SpeakingChatbotV2Assistant';
import SubProjectAgentPanel from '../components/SubProjectAgentPanel';
import RightPaneContentSwitcher, { type DocumentTab } from '../components/RightPaneContentSwitcher';
import SolutionsBox from '../components/SolutionsBox';
import SolutionProgressMatrix from '../components/SolutionProgressMatrix';
import type { SearchResultsData, SearchResultDocument } from '../components/SearchResultsTab';
import { useRagManagement } from '../hooks/useRagManagement';
import { useDocumentUpload } from '../hooks/useDocumentUpload';
import { useSolutions } from '../hooks/useSolutions';
import { useSearchActions } from '../hooks/useSearchActions';

import { getWatchStatus, type WatchStatus } from '../services/techWatchService';
import { getPendingSearchResults, removeSearchResults } from '../utils/searchResultsStorage';
import { userActivityService } from '../services/userActivityService';
import type { EntitySCV2Config } from '../configs/entitySCV2Config';
import SCV2Modals from '../components/SCV2Modals';
import ExtractionLaunchModal from '../components/ExtractionLaunchModal';
import OpsRetrievalModal from '../components/OpsRetrievalModal';
import OpenalexRetrievalModal from '../components/OpenalexRetrievalModal';
import RAGPrepareCostDialog from '../components/RAGPrepareCostDialog';
import { projectSCV2Config } from '../configs/projectSCV2Config';
import { subProjectSCV2Config } from '../configs/subProjectSCV2Config';
import { kbSCV2Config } from '../configs/kbSCV2Config';
import './SpeakingChatbotV2.css';

type Entity = Project | SubProject;

const SOLUTION_DATA_FIELDS_FE = new Set(['solution_description', 'claims', 'fto_analysis', 'industrialization', 'patentability', 'patent_application']);

// Config registry
const ENTITY_CONFIGS: Record<string, EntitySCV2Config> = {
  'project': projectSCV2Config as EntitySCV2Config,
  'sub-project': subProjectSCV2Config as EntitySCV2Config,
  'kb': kbSCV2Config as EntitySCV2Config,
};

const MAX_VISIBLE_KB_BUTTONS = 4;

function kbInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map(w => w[0].toUpperCase())
    .join('')
    .slice(0, 4);
}

interface KbNavButtonsProps {
  kbs: KnowledgeBase[];
  onOpen: (kb: KnowledgeBase) => void;
}

const KbNavButtons: React.FC<KbNavButtonsProps> = ({ kbs, onOpen }) => {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!overflowOpen) return;
    const handler = (e: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node))
        setOverflowOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [overflowOpen]);

  const visible = kbs.slice(0, MAX_VISIBLE_KB_BUTTONS);
  const overflow = kbs.slice(MAX_VISIBLE_KB_BUTTONS);

  return (
    <div className="kb-nav-buttons">
      <span className="kb-nav-label">KB(s):</span>
      <div className="kb-nav-btn-grid">
        {visible.map(kb => (
          <button
            key={kb.id}
            className="kb-nav-btn"
            title={kb.name}
            onClick={() => onOpen(kb)}
          >
            <span className="kb-nav-abbr">{kbInitials(kb.name)}</span>
            <span className="kb-nav-count">{kb.document_count} docs</span>
          </button>
        ))}
      </div>
      {overflow.length > 0 && (
        <div className="kb-nav-overflow" ref={overflowRef}>
          <button
            className="kb-nav-btn-more"
            title={`${overflow.length} more KB${overflow.length > 1 ? 's' : ''}`}
            onClick={() => setOverflowOpen(o => !o)}
          >
            +{overflow.length}
          </button>
          {overflowOpen && (
            <div className="kb-nav-dropdown">
              {overflow.map(kb => (
                <button
                  key={kb.id}
                  className="kb-nav-dropdown-item"
                  onClick={() => { onOpen(kb); setOverflowOpen(false); }}
                >
                  <span className="kb-nav-dropdown-name">{kb.name}</span>
                  <span className="kb-nav-count">{kb.document_count} docs</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SpeakingChatbotV2: React.FC = () => {
  const { entityType, entityId } = useParams<{ entityType: string; entityId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const canEditProjects = useCanEditProjects();
  const canDeleteProject = useCanDeleteProject();
  const canDeleteKnowledgeBase = useCanDeleteKnowledgeBases();
  const canCreateSubprojects = useCanCreateSubprojects();
  const canPrepareRag = useCanPrepareRag();
  const canViewDocuments = useCanViewDocuments();
  const { user } = useAuth();
  const { addToast } = useToast();
  const showMetrics = user?.role === 'SysAdmin' || user?.role === 'DevOps';

  // Get entity config
  const config = entityType ? ENTITY_CONFIGS[entityType] : null;

  // Entity state
  const [entity, setEntity] = useState<Entity | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ path: string; label: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kbName, setKbName] = useState<string>('');
  const [selectedKbId, setSelectedKbId] = useState<string | null>(null);
  const [parentProject, setParentProject] = useState<Project | null>(null);

  // UI state
  const [activeTab, setActiveTab] = useState<string>(entityType === 'kb' ? 'documents' : 'metadata');
  const [activeField, setActiveField] = useState<string | null>(() => {
    // Restore last CF from localStorage for sub-projects; null on first visit (no stored key)
    if (entityType === 'sub-project' && entityId) {
      return localStorage.getItem(`rda_last_cf_${entityId}`) || null;
    }
    return null;
  });
  const [expandedFields, setExpandedFields] = useState<Record<string, boolean>>({
    description: true, // Default expanded
    problem_statement: false, // SP page: collapsed on open
    project_features: false, // SP page: collapsed on open
    kb_ids_list: true,
  });
  const [highlightedFields, setHighlightedFields] = useState<Record<string, boolean>>({});

  // Solutions state (sub-projects only)
  const [solutions, setSolutions] = useState<SolutionModel[]>([]);
  const [activeSolutionId, setActiveSolutionId] = useState<string | null>(() => {
    if (entityType === 'sub-project' && entityId) {
      return localStorage.getItem(`rda_active_solution_${entityId}`) || null;
    }
    return null;
  });

  // Resume briefing state (sub-projects only)
  const [resumeBriefing, setResumeBriefing] = useState<string | null>(null);

  // Document tabs
  const [documents, setDocuments] = useState<DocumentTab[]>([]);
  const [selectedKbDocumentIds, setSelectedKbDocumentIds] = useState<string[]>([]);
  const [kbDocNumberMap, setKbDocNumberMap] = useState<Map<string, number>>(new Map());
  
  // Search results tabs (Docs1, Docs2, etc.) - MUST be declared before computedDocumentIds
  const [searchResultsTabs, setSearchResultsTabs] = useState<SearchResultsData[]>([]);
  const sessionStorageLoadedRef = useRef<boolean>(false); // Track if sessionStorage was already loaded
  
  // Search tab selections: Map<tabId, Set<documentId>>
  const [searchTabSelections, setSearchTabSelections] = useState<Map<string, Set<string>>>(new Map());

  const handleSearchTabSelectionChange = (tabId: string, documentIds: string[]) => {
    setSearchTabSelections(prev => {
      const next = new Map(prev);
      next.set(tabId, new Set(documentIds));
      return next;
    });
  };

  const handleKbSelectionChange = useCallback((selectedIds: string[]) => {
    setSelectedKbDocumentIds(selectedIds);
    setKbDocNumberMap(prev => {
      const next = new Map(prev);
      let nextNum = next.size + 1;
      for (const id of selectedIds) {
        if (!next.has(id)) {
          next.set(id, nextNum++);
        }
      }
      return next;
    });
  }, []);

  const computedDocumentIds = useMemo(() => {
    const activeSearchTab = searchResultsTabs.find(tab => tab.tabId === activeTab);
    if (activeSearchTab) {
      const searchSelections = searchTabSelections.get(activeTab);
      return searchSelections ? Array.from(searchSelections) : [];
    }
    const kbDocs = selectedKbDocumentIds;
    const openDocs = documents.map(d => d.id);
    return kbDocs.length > 0 ? kbDocs : openDocs;
  }, [selectedKbDocumentIds, documents, activeTab, searchResultsTabs, searchTabSelections]);

  // Search sessions and KB data (for sub-projects)
  const [searchSessionsData, setSearchSessionsData] = useState<unknown[]>([]);
  const [kbsData, setKbsData] = useState<KnowledgeBase[]>([]);
  const [contextKbsData, setContextKbsData] = useState<Record<string, { name: string; document_count: number }>>({});

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSubProjectModal, setShowSubProjectModal] = useState(false);
  const [showSearchServiceModal, setShowSearchServiceModal] = useState(false);
  const [showExtractionModal, setShowExtractionModal] = useState(false);
  const [showOpsRetrievalModal, setShowOpsRetrievalModal] = useState(false);
  const [showOpenalexRetrievalModal, setShowOpenalexRetrievalModal] = useState(false);
  const [extractionRegime, setExtractionRegime] = useState<'A' | 'B'>('B');
  const [showViewSubProjectsModal, setShowViewSubProjectsModal] = useState(false);
  const [subProjectsList, setSubProjectsList] = useState<SubProject[]>([]);
  const [loadingSubProjects, setLoadingSubProjects] = useState(false);
  const [subProjectFormData, setSubProjectFormData] = useState({
    name: '',
    description: '',
  });

  // Context KB modal state
  const [showContextKbModal, setShowContextKbModal] = useState(false);
  const [contextKbConfig, setContextKbConfig] = useState<{ context: string; defaultName: string; tabName: string } | null>(null);

  // RAG management (KB only) — via hook
  const {
    ragStatus, ragPrepareLoading,
    showRagCostDialog, setShowRagCostDialog,
    handlePrepareRag, executePrepareRag,
  } = useRagManagement(entityType || '', entityId, canViewDocuments, (entity as { kb_class?: string } | null)?.kb_class);

  // Document upload state (KB only) — via hook
  const {
    showAddDocModal, setShowAddDocModal,
    uploadMode, setUploadMode,
    selectedFiles, setSelectedFiles,
    pageUrl, setPageUrl,
    uploading,
    kbDocumentsTabRef,
    handleUploadDocuments,
  } = useDocumentUpload(entityType || '', entityId, config, setEntity as React.Dispatch<React.SetStateAction<unknown>>);

  // Tech Watch state (sub-project only)
  const [showTechWatchConfig, setShowTechWatchConfig] = useState(false);
  const [showTechWatchDigest, setShowTechWatchDigest] = useState(false);
  const [techWatchStatus, setTechWatchStatus] = useState<WatchStatus | null>(null);

  // Save state
  const saveTimeoutRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Load entity and documents
  useEffect(() => {
    const loadData = async () => {
      if (!entityId || !config) return;

      try {
        setLoading(true);
        sessionStorageLoadedRef.current = false; // Reset on new entity load

        // Load entity using config service
        const entityData = await config.service.get(entityId);
        setEntity(entityData as Entity);

        // Log activity for recently-accessed tracking
        const activityType = entityType === 'sub-project' ? 'sub_project' : entityType as 'project' | 'kb';
        userActivityService.logActivity({ entity_type: activityType, entity_id: entityId });

        // Build breadcrumbs
        const crumbs = await config.buildBreadcrumbs(entityData);
        setBreadcrumbs(crumbs);

        // Load KB name if available
        const kbIds = config.getKbIds(entityData);
        if (kbIds?.[0]) {
          setSelectedKbId(kbIds[0]);
          try {
            const kb = await knowledgeBaseService.getById(kbIds[0]);
            setKbName(kb.name || '');
          } catch (e) {
            console.warn('Failed to load KB name:', e);
          }
        }

        // Load documents from URL params
        const docIdsParam = searchParams.get('documentIds');
        if (docIdsParam && kbIds?.[0]) {
          try {
            const result = await knowledgeBaseService.getDocumentsWithDetails(kbIds[0]);
            const docDetails = result?.documents || [];
            const docIds = docIdsParam.split(',');

            const loadedDocs: DocumentTab[] = [];
            for (const id of docIds) {
              const detail = docDetails.find((d) => d.id === id);
              if (detail) {
                // Build PDF URL from the API endpoint
                const pdfUrl = `/api/knowledge-bases/${kbIds[0]}/documents/${id}/pdf`;
                loadedDocs.push({
                  id,
                  title: detail.display_name || detail.full_filename || `Doc ${id.substring(0, 6)}`,
                  url: pdfUrl,
                  kbId: kbIds[0],
                  chunks: [],
                });
              }
            }
            setDocuments(loadedDocs);
          } catch (e) {
            console.error('Failed to load documents:', e);
          }
        }

        // Load search sessions data for sub-projects
        if (entityType === 'sub-project' && (entityData as Record<string, unknown>).search_sessions) {
          try {
            const sessions = await searchService.getSessionsForSubProject(entityId);
            setSearchSessionsData(sessions);
          } catch (e) {
            console.warn('Failed to load search sessions:', e);
          }
        }

        // Load KB data for sub-projects
        if (entityType === 'sub-project' && kbIds && kbIds.length > 0) {
          try {
            const kbPromises = kbIds.map(id => knowledgeBaseService.getById(id));
            const kbs = await Promise.all(kbPromises);
            setKbsData(kbs);
          } catch (e) {
            console.warn('Failed to load KB data:', e);
          }
        }

        // Load context KB data for sub-projects (for CFKB badge enrichment)
        if (entityType === 'sub-project' && (entityData as Record<string, unknown>).context_kbs) {
          try {
            const contextKbs = (entityData as Record<string, unknown>).context_kbs as Record<string, string>;
            const uniqueKbIds = [...new Set(Object.values(contextKbs).filter(Boolean))] as string[];
            if (uniqueKbIds.length > 0) {
              const kbs = await Promise.all(uniqueKbIds.map(id => knowledgeBaseService.getById(id)));
              const map: Record<string, { name: string; document_count: number }> = {};
              kbs.forEach(kb => { map[kb.id] = { name: kb.name, document_count: kb.document_count || 0 }; });
              setContextKbsData(map);
            }
          } catch (e) {
            console.warn('Failed to load context KB data:', e);
          }
        }

        // Load Tech Watch status for sub-projects
        if (entityType === 'sub-project' && entityId) {
          getWatchStatus(entityId)
            .then(res => setTechWatchStatus(res.data))
            .catch(() => setTechWatchStatus(null));
        }

        // Load parent project for sub-projects (to display project_features read-only)
        if (entityType === 'sub-project') {
          const projectId = (entityData as SubProject).project_id;
          if (projectId) {
            try {
              const pp = await projectService.getProject(projectId);
              setParentProject(pp);
            } catch (e) {
              console.warn('Failed to load parent project:', e);
            }
          }
        }

        // Load solutions for sub-projects
        if (entityType === 'sub-project' && entityId) {
          try {
            const sols = await solutionService.list(entityId);
            setSolutions(sols);
            if (sols.length > 0) {
              const stored = localStorage.getItem(`rda_active_solution_${entityId}`);
              const validId = stored && sols.find(s => s.id === stored) ? stored : sols[0].id;
              setActiveSolutionId(validId);
              if (!stored || stored !== validId) {
                localStorage.setItem(`rda_active_solution_${entityId}`, validId);
              }
            }
          } catch (e) {
            console.warn('Failed to load solutions:', e);
          }

          // Fetch resume briefing (non-blocking)
          import('../services/spHistoryService').then(({ spHistoryService }) => {
            spHistoryService.getBriefing(entityId, i18n.language)
              .then(resp => {
                if (resp.briefing) {
                  setResumeBriefing(resp.briefing);
                } else if (resp.cold_start) {
                  const coldStartMsg = i18n.language === 'fr'
                    ? 'Bienvenue ! Pour commencer, je vous propose de definir le probleme technique, puis nous ferons un etat de l\'art avant de travailler sur les solutions.'
                    : 'Welcome! To get started, I suggest defining the technical problem first, then we\'ll survey the state of the art before working on solutions.';
                  setResumeBriefing(coldStartMsg);
                }
              })
              .catch(e => console.warn('Failed to load briefing:', e));
          });
        }

        // Load pending search results from sessionStorage (sub-projects only)
        if (entityType === 'sub-project' && entityId && !sessionStorageLoadedRef.current) {
          const pendingResults = getPendingSearchResults(entityId);
          if (pendingResults.length > 0) {
            setSearchResultsTabs(pendingResults);
            // Only auto-switch to the search results tab when navigating directly from a search page
            const navigatedFromSearch = (location.state as Record<string, unknown>)?.fromSearch === true;
            if (navigatedFromSearch) {
              const latestTabId = pendingResults[pendingResults.length - 1].tabId;
              setTimeout(() => setActiveTab(latestTabId), 0);
            }
            sessionStorageLoadedRef.current = true;
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : `Failed to load ${config?.displayName || 'entity'}`);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [entityId, entityType, config, searchParams]);

  // Handle field value change (from user typing)
  const handleFieldChange = useCallback((fieldName: string, value: string) => {
    if (!entity) return;

    // state_of_the_art is an object: update only the .content property
    if (fieldName === 'state_of_the_art') {
      const currentSota = (entity as unknown as Record<string, unknown>).state_of_the_art || {};
      setEntity(prev => prev ? { ...prev, state_of_the_art: { ...currentSota, content: value } } as unknown as Entity : null);
    } else {
      setEntity(prev => prev ? { ...prev, [fieldName]: value } : null);
    }
    setHighlightedFields(prev => ({ ...prev, [fieldName]: true }));

    // Debounced save
    if (saveTimeoutRef.current[fieldName]) {
      clearTimeout(saveTimeoutRef.current[fieldName]);
    }

    saveTimeoutRef.current[fieldName] = setTimeout(() => {
      saveField(fieldName, value);
    }, 2000);
  }, [entity, entityId, config]);

  // Save field to database
  const saveField = async (fieldName: string, value: string) => {
    if (!entityId || !config) return;

    try {
      let updatePayload: Record<string, unknown>;
      if (fieldName === 'state_of_the_art') {
        const currentSota = entity ? (entity as unknown as Record<string, { version?: number; generation_status?: string }>).state_of_the_art : null;
        const currentStatus = currentSota?.generation_status ?? 'empty';
        let newStatus: string;
        if (!value.trim()) {
          newStatus = 'empty';
        } else if (currentStatus === 'enriched' || currentStatus === 'finalized') {
          // Manual edit on enriched/finalized resets to enriched
          newStatus = 'enriched';
        } else if (currentStatus === 'scoping' || currentStatus === 'draft') {
          // Manual edit preserves current status (user is refining)
          newStatus = currentStatus;
        } else {
          newStatus = 'draft';
        }
        updatePayload = {
          state_of_the_art: {
            content: value,
            version: currentSota?.version ?? 1,
            generation_status: newStatus,
          },
        };
      } else {
        updatePayload = { [fieldName]: value };
      }
      await config.service.update(entityId, updatePayload);
    } catch (err) {
      console.error(`Failed to save field ${fieldName}:`, err);
    }
  };

  // Handle AI-driven field update
  const handleAIFieldUpdate = useCallback((fieldName: string, value: string) => {
    if (!entity) return;

    // Solution fields: update active solution in local state (backend already saved via tool call)
    if (SOLUTION_DATA_FIELDS_FE.has(fieldName) && activeSolutionId) {
      setSolutions(prev => prev.map(s => s.id === activeSolutionId ? { ...s, [fieldName]: value } : s));
      setHighlightedFields(prev => ({ ...prev, [fieldName]: true }));
      setExpandedFields(prev => ({ ...prev, [fieldName]: true }));
      return;
    }

    setEntity(prev => prev ? { ...prev, [fieldName]: value } : null);
    setHighlightedFields(prev => ({ ...prev, [fieldName]: true }));
    setExpandedFields(prev => ({ ...prev, [fieldName]: true }));

    // Save immediately for AI updates
    saveField(fieldName, value);
  }, [entity, entityId, config, activeSolutionId]);

  // Toggle field expansion
  const toggleFieldExpand = (fieldName: string) => {
    setExpandedFields(prev => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };

  // Handle field focus — sets the active CFA, expands it, collapses all others
  const handleFieldFocus = (fieldName: string) => {
    setActiveField(fieldName);
    setExpandedFields(prev => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach(k => { next[k] = false; });
      next[fieldName] = true;
      return next;
    });
    if (entityType === 'sub-project' && entityId) {
      localStorage.setItem(`rda_last_cf_${entityId}`, fieldName);
    }
  };

  // Handle CFA hand-over initiated by the LLM (backend tool call)
  const handleContextFieldChanged = (targetField: string | null) => {
    if (!targetField) return;
    setActiveField(targetField);
    setExpandedFields(prev => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach(k => { next[k] = false; });
      next[targetField] = true;
      return next;
    });
    if (entityType === 'sub-project' && entityId) {
      localStorage.setItem(`rda_last_cf_${entityId}`, targetField);
    }
    // Scroll metadata tab into view and switch to metadata tab if not already there
    setActiveTab('metadata');
  };

  // Create or update a Docs tab when extraction_results arrives from chatbot
  const handleExtractionResults = useCallback((data: unknown) => {
    const { tabId, tabName, kbId: tabKbId, kbName: tabKbName, destinationKbId: tabDestKbId, destinationKbName: tabDestKbName, documents, timestamp } = data as { tabId?: string; tabName?: string; kbId?: string; kbName?: string; destinationKbId?: string; destinationKbName?: string; documents?: Array<{ document_id: string; document_title?: string; relevance_score?: number; justification?: string }>; timestamp?: string };
    if (!tabId || !tabName) return;

    const mappedDocs = (documents || []).map((d) => ({
      document_id: d.document_id,
      document_title: d.document_title,
      relevance_score: d.relevance_score ?? 0,
      justification: d.justification,
    }));

    const existingTab = searchResultsTabs.find(t => t.tabId === tabId);
    if (existingTab) {
      // Update placeholder tab (e.g. created by KB launch) with actual results
      setSearchResultsTabs(prev => prev.map(t =>
        t.tabId === tabId ? { ...t, documents: mappedDocs, tabName } : t
      ));
      setActiveTab(tabId);
      return;
    }

    const newTab: SearchResultsData = {
      tabId,
      tabName,
      kbId: tabKbId || '',
      kbName: tabKbName || '',
      destinationKbId: tabDestKbId,
      destinationKbName: tabDestKbName,
      searchType: 'extraction',
      documents: mappedDocs,
      timestamp: timestamp || new Date().toISOString(),
      isSaved: false,
    };
    setSearchResultsTabs(prev => [...prev, newTab]);
    setActiveTab(tabId);
  }, [searchResultsTabs]);

  // Handle field blur
  const handleFieldBlur = () => {
    // Keep activeField set so AI knows the last focused field
  };

  // Handle delete entity
  const handleDeleteEntity = async () => {
    if (!entityId || !config || !entity) return;
    try {
      if (config.service.delete) {
        await config.service.delete(entityId);
        setShowDeleteModal(false); // Close modal before navigation
        navigate(config.getParentPath(entity));
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : `Failed to delete ${config.displayName}`);
    }
  };

  // Handle create sub-project (only for Project entity type)
  const handleCreateSubProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!entityId || entityType !== 'project') return;
    try {
      const data: CreateSubProjectRequest = {
        name: subProjectFormData.name,
        problem_statement: subProjectFormData.description || undefined,
        project_id: entityId,
      };
      await subProjectService.create(data);
      setShowSubProjectModal(false);
      setSubProjectFormData({ name: '', description: '' });
      // Navigate to the project page to see the new sub-project
      navigate(`/project/${entityId}`);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create sub-project');
    }
  };

  // Handle new search (only for SubProject entity type)
  const handleNewSearch = () => {
    setShowSearchServiceModal(true);
  };

  const handleSearchServiceSelect = (service: string) => {
    setShowSearchServiceModal(false);
    if (service === 'patent-search') {
      navigate(`/semantic-search/${entityId}`);
    } else if (service === 'extraction-fast') {
      setExtractionRegime('B');
      setShowExtractionModal(true);
    } else if (service === 'extraction-slow') {
      setExtractionRegime('A');
      setShowExtractionModal(true);
    } else if (service === 'ops-direct') {
      setShowOpsRetrievalModal(true);
    } else if (service === 'openalex-direct') {
      setShowOpenalexRetrievalModal(true);
    } else if (service === 'cpc-search') {
      navigate(`/cpc-search/${entityId}`);
    }
  };

  // Close document tab
  const closeDocumentTab = (docId: string) => {
    // Check if it's a search results tab
    const searchTab = searchResultsTabs.find(t => t.tabId === docId);
    if (searchTab) {
      // Warn if unsaved
      if (!searchTab.isSaved) {
        if (!window.confirm('This search result has not been saved. Close anyway?')) {
          return;
        }
      }
      // Remove from sessionStorage and state
      if (entityId) {
        removeSearchResults(entityId, docId);
      }
      setSearchResultsTabs(prev => prev.filter(t => t.tabId !== docId));
    } else {
      // Regular document tab
      setDocuments(prev => prev.filter(d => d.id !== docId));
    }
    
    if (activeTab === docId) {
      setActiveTab('metadata');
    }
  };

  // Search actions via hook
  const { handleSaveSearchResults, handleSaveSearch, handleSelectSearch } = useSearchActions(
    entityId, selectedKbId, kbName, searchResultsTabs, setSearchResultsTabs, setKbsData as React.Dispatch<React.SetStateAction<unknown[]>>, setActiveTab,
  );

  const handleDeleteSelectedKbDocuments = async () => {
    if (entityType !== 'kb') return;
    if (!selectedKbDocumentIds.length) {
      addToast({
        type: 'info',
        title: t('kbDetail.noSelectionTitle', 'No documents selected'),
        message: t('kbDetail.noSelectionMsg', 'Select documents in the Documents tab first.'),
      });
      return;
    }

    const confirmDelete = window.confirm(
      t('kbDetail.confirmDeleteDocuments', 'Delete selected document(s) from this knowledge base?')
    );
    if (!confirmDelete) return;

    const deletedIds = await kbDocumentsTabRef.current?.deleteSelected();
    if (!deletedIds || deletedIds.length === 0) {
      return;
    }

    setSelectedKbDocumentIds([]);
    setKbsData(prev => prev.map(kb => {
      if (kb.id !== entityId) return kb;
      const currentCount = Number(kb.document_count || 0);
      return { ...kb, document_count: Math.max(0, currentCount - deletedIds.length) };
    }));

    setEntity(prev => {
      if (!prev) return prev;
      const currentCount = Number((prev as unknown as Record<string, unknown>).document_count || 0);
      return { ...prev, document_count: Math.max(0, currentCount - deletedIds.length) } as unknown as Entity;
    });
  };

  // Handle click on document in search results
  const handleSearchDocumentClick = (documentId: string, document: SearchResultDocument) => {
    const containingTab = searchResultsTabs.find(tab => 
      tab.documents.some(d => d.document_id === documentId)
    );
    const kbId = document.kb_id || containingTab?.kbId;
    if (!kbId) return;

    const newTab: DocumentTab = {
      id: documentId,
      title: document.document_title || documentId.substring(0, 12) + '...',
      kbId: kbId,
      chunks: document.chunks,
      isPDFWithChunks: true,
    };

    setDocuments(prev => prev.some(d => d.id === documentId) ? prev : [...prev, newTab]);
    setActiveTab(documentId);
  };

  // Handle view sub-projects
  const handleViewSubProjects = async () => {
    if (entityType !== 'project') return;
    
    setShowViewSubProjectsModal(true);
    setLoadingSubProjects(true);
    
    try {
      const subProjects = await subProjectService.getByProjectId(entityId!);
      setSubProjectsList(subProjects);
    } catch (err) {
      console.error('Failed to load sub-projects:', err);
      alert('Failed to load sub-projects');
    } finally {
      setLoadingSubProjects(false);
    }
  };

  // Navigate to sub-project V2 page
  const handleSubProjectCardClick = (subProjectId: string) => {
    setShowViewSubProjectsModal(false);
    navigate(`/scv2/sub-project/${subProjectId}`);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!config) return dateString;
    return config.formatDate(dateString, i18n.language);
  };

  // Get field value as string
  const getFieldValue = (fieldName: string): string => {
    if (!entity || !config) return '';
    const value = (entity as unknown as Record<string, unknown>)[fieldName];
    if (value === null || value === undefined) return '';
    if (fieldName === 'created_date' || fieldName === 'last_updated') {
      return formatDate(value as string);
    }
    // Handle features_list object
    if (fieldName === 'features_list') {
      const featVal = value as { features?: { feature_name?: string }[] } | null | undefined;
      if (typeof featVal === 'object' && featVal?.features && Array.isArray(featVal.features)) {
        if (featVal.features.length === 0) {
          return 'No features defined yet.';
        }
        return featVal.features.map((f: { feature_name?: string }, idx: number) => 
          `${idx + 1}. ${f.feature_name || 'Unnamed Feature'}`
        ).join('\n');
      }
      return 'No features available.';
    }

    // Handle search_sessions_list
    if (fieldName === 'search_sessions_list') {
      if (searchSessionsData.length === 0) {
        return 'No searches performed yet.';
      }
      return searchSessionsData.map((sessionItem, idx: number) => {
        const session = sessionItem as Record<string, unknown>;
        const date = new Date(session.created_date as string).toLocaleDateString();
        return `${idx + 1}. ${session.session_name || 'Unnamed Search'} (${date})`;
      }).join('\n');
    }

    // Handle kb_ids_list 
    if (fieldName === 'kb_ids_list') {
      if (kbsData.length === 0) {
        return 'No knowledge bases linked.';
      }
      return kbsData.map((kb, idx: number) => {
        const docCount = kb.document_count || 0;
        return `${idx + 1}. ${kb.name} (${docCount} documents)`;
      }).join('\n');
    }

    return String(value);
  };

  // Get visible fields from config (currently unused)
  // const visibleFields = config ? Object.entries(config.fieldsPermissions)
  //   .filter(([_, permission]) => permission !== 'none')
  //   .map(([fieldName, permission]) => ({
  //     fieldName,
  //     permission,
  //     ...(config.fieldLabels[fieldName] || { label: fieldName, description: '' }),
  //   })) : [];

  const kbIds = entity && config ? config.getKbIds(entity) : [];

  // Sync selected KB with loaded list and keep kbName aligned (must stay before conditional returns)
  useEffect(() => {
    if (!kbsData || kbsData.length === 0) {
      setSelectedKbId(null);
      setKbName('');
      return;
    }

    const found = selectedKbId ? kbsData.find(kb => kb.id === selectedKbId) : null;
    const active = found || kbsData[0];
    if (!found && active?.id && active.id !== selectedKbId) {
      setSelectedKbId(active.id);
    }
    setKbName(active?.name || '');
  }, [kbsData, selectedKbId]);

  const handleKbSelect = (kbId: string) => {
    setSelectedKbId(kbId);
    const kb = kbsData.find(k => k.id === kbId);
    if (kb) {
      setKbName(kb.name || '');
    }
  };

  // Solution handlers via hook
  const { handleSolutionChange, handleSolutionFieldChange, handleAddSolution } = useSolutions(
    entityId, solutions, setSolutions, activeSolutionId, setActiveSolutionId, setHighlightedFields,
  );

  // Open context KB creation modal
  const handleCreateContextKb = (context: string, defaultName: string, tabName: string) => {
    setContextKbConfig({ context, defaultName, tabName });
    setShowContextKbModal(true);
  };

  // Called after a context KB is successfully created
  const handleContextKbCreated = async (kb: KnowledgeBase) => {
    if (!contextKbConfig || !entityId) return;
    setShowContextKbModal(false);
    const { context } = contextKbConfig;

    // Update entity state immediately
    setEntity(prev => {
      if (!prev) return prev;
      const currentSota = (prev as unknown as Record<string, unknown>).context_kbs || {};
      return { ...prev, context_kbs: { ...(currentSota as Record<string, string>), [context]: kb.id } } as Entity;
    });

    // Update contextKbsData with the new KB
    setContextKbsData(prev => ({ ...prev, [kb.id]: { name: kb.name, document_count: kb.document_count || 0 } }));

    // Persist to backend
    try {
      const currentContextKbs = (entity as unknown as Record<string, unknown>)?.context_kbs || {};
      await subProjectService.update(entityId, {
        context_kbs: { ...currentContextKbs, [context]: kb.id },
      });
    } catch (err) {
      console.error('Failed to save context_kbs:', err);
      addToast({ type: 'error', title: 'Error', message: 'Failed to link KB to context.' });
    }

    setContextKbConfig(null);
  };

  // Open a context KB as a Documents tab (like search results)
  const handleOpenKbTab = async (kb: KnowledgeBase) => {
    const tabId = `kb-${kb.id}`;
    const existingTab = searchResultsTabs.find(t => t.tabId === tabId);
    if (existingTab) { setActiveTab(tabId); return; }
    try {
      const result = await knowledgeBaseService.getDocumentsWithDetails(kb.id);
      const docs = result?.documents || [];
      const tabDocuments: SearchResultDocument[] = docs.map((doc) => ({
        document_id: doc.id,
        document_title: doc.display_name || doc.full_filename || doc.id,
        relevance_score: typeof doc.relevance === 'number' ? doc.relevance : 0,
        kb_id: kb.id,
        kb_name: kb.name,
        chunks: [],
      }));
      const newTab: SearchResultsData = {
        tabId,
        tabName: kb.name,
        documents: tabDocuments,
        searchType: 'KB',
        kbId: kb.id,
        kbName: kb.name,
        timestamp: new Date().toISOString(),
        isSaved: true,
        isSearchSaved: true,
      };
      setSearchResultsTabs(prev => [...prev, newTab]);
      setActiveTab(tabId);
    } catch (err) {
      console.error('Failed to load KB documents:', err);
      addToast({ type: 'error', title: 'Error', message: 'Failed to open KB documents.' });
    }
  };

  const handleOpenContextKbTab = async (kbId: string, tabName: string) => {
    // Switch to existing tab if already open
    const existingTab = searchResultsTabs.find(t => t.tabId === `ctx-kb-${kbId}`);
    if (existingTab) {
      setActiveTab(existingTab.tabId);
      return;
    }

    try {
      const result = await knowledgeBaseService.getDocumentsWithDetails(kbId);
      const docs = result?.documents || [];

      const tabDocuments: SearchResultDocument[] = docs.map((doc) => ({
        document_id: doc.id,
        document_title: doc.display_name || doc.full_filename || doc.id,
        relevance_score: typeof doc.relevance === 'number' ? doc.relevance : 0,
        kb_id: kbId,
        kb_name: result.kb_name,
        chunks: [],
      }));

      const newTab: SearchResultsData = {
        tabId: `ctx-kb-${kbId}`,
        tabName,
        documents: tabDocuments,
        searchType: 'KB',
        kbId,
        kbName: result.kb_name,
        timestamp: new Date().toISOString(),
        isSaved: true,
        isSearchSaved: true,
      };

      setSearchResultsTabs(prev => [...prev, newTab]);
      setActiveTab(newTab.tabId);
    } catch (err) {
      console.error('Failed to load context KB documents:', err);
      addToast({ type: 'error', title: 'Error', message: 'Failed to open KB documents.' });
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-slate-400 text-sm">
      <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mr-2" />
      Opening Workspace...
    </div>
  );
  if (error || !entity || !config) return <div className="flex items-center justify-center h-64 text-destructive text-sm">{error || 'Entity not found'}</div>;

  const entityName = config.getEntityName(entity);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Top Banner */}
      <div className="border-b border-slate-300 bg-slate-100 px-4 py-2 flex items-center gap-3 flex-shrink-0 min-w-0">
        {/* Left: breadcrumbs + KB section */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <nav className="flex items-center gap-1 text-xs text-slate-600 font-medium flex-shrink-0 flex-wrap">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.path}>
                {index > 0 && <span className="text-slate-300 mx-1">·</span>}
                <Link to={crumb.path}>{crumb.label}</Link>
              </React.Fragment>
            ))}
          </nav>
          {entityType === 'sub-project' && kbsData.length > 0 && (
            <KbNavButtons kbs={kbsData} onOpen={handleOpenKbTab} />
          )}
        </div>
        {/* Right: tech-watch + action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {entityType === 'sub-project' && (
            <>
              <span className="text-xs text-slate-500 cursor-pointer whitespace-nowrap"
                onClick={() => techWatchStatus?.exists ? setShowTechWatchDigest(!showTechWatchDigest) : setShowTechWatchConfig(true)}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                  background: techWatchStatus?.is_active ? '#16a34a' : '#9ca3af', marginRight: 4 }} />
                {techWatchStatus?.is_active ? t('techWatch.active') : t('techWatch.inactive')}
              </span>
              {techWatchStatus?.has_pending_digest && (
                <span className="text-xs text-primary-600 cursor-pointer font-semibold whitespace-nowrap"
                  onClick={() => setShowTechWatchDigest(true)}>
                  {t('techWatch.summaryAvailable')}
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {(() => {
            const deletePermission = entityType === 'kb' ? canDeleteKnowledgeBase : canDeleteProject;
            return config.buildActionButtons(
              entity,
              {
                onDelete: () => setShowDeleteModal(true),
                onCreate: () => setShowSubProjectModal(true),
                onNewSearch: entityType === 'sub-project' ? handleNewSearch : undefined,
                onTechWatch: entityType === 'sub-project' ? () => setShowTechWatchConfig(true) : undefined,
                techWatchExists: entityType === 'sub-project' ? (techWatchStatus?.exists ?? false) : false,
                onViewKBs: () => {
                  // TODO: Implement View Knowledge Bases navigation
                  console.log('View KBs clicked - to be implemented');
                },
                onViewSubProjects: handleViewSubProjects,
                // KB-specific handlers
                onPrepareRag: handlePrepareRag,
                onAddDocuments: () => {
                  setShowAddDocModal(true);
                  setUploadMode('patent');
                  setPageUrl('');
                  setSelectedFiles(null);
                },
                onDeleteSelectedDocuments: handleDeleteSelectedKbDocuments,
              },
              {
                canEdit: canEditProjects,
                canDelete: deletePermission,
                canCreate: canCreateSubprojects,
              },
              t,
              // KB action state (only relevant for KB entity type)
              entityType === 'kb' ? {
                ragStatus: ragStatus?.status,
                ragProgress: ragStatus?.progress ?? null,
                ragMessage: ragStatus?.message ?? null,
                ragVectorStatus: ragStatus?.vector_index_status ?? null,
                ragBm25Status: ragStatus?.bm25_index_status ?? null,
                ragEstimatedCompletion: ragStatus?.estimated_completion ?? null,
                ragPrepareLoading,
                canPrepareRag,
                canViewDocuments,
                selectedDocumentCount: selectedKbDocumentIds.length,
              } : undefined
            );
          })()}
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Assistant Pane (Left) */}
        <div className="w-[30%] flex-shrink-0 flex flex-col border-r border-slate-200 min-h-0 overflow-hidden">
          {entityType === 'sub-project' ? (
            <SubProjectAgentPanel
              subProjectId={entityId!}
              subProjectName={entityName}
              kbId={selectedKbId || kbIds?.[0] || undefined}
              kbName={kbName}
              solutionId={activeSolutionId}
              language={i18n.language as 'en' | 'fr'}
              showMetrics={showMetrics}
              onActiveFieldChange={handleContextFieldChanged}
              onSolutionIdChange={setActiveSolutionId}
              onOpenWorkspace={(field, solId) => {
                const params = new URLSearchParams();
                const sol = solId || activeSolutionId;
                if (sol) params.set('solutionId', sol);
                navigate(`/workspace/${entityId}/${field}${params.size ? '?' + params.toString() : ''}`);
              }}
            />
          ) : (
            <SpeakingChatbotV2Assistant
              projectId={(() => {
                if (entityType === 'kb' && entity && 'project_id' in entity) {
                  return (entity as unknown as Record<string, unknown>).project_id as string | undefined || undefined;
                } else if (entityType === 'project') {
                  return entityId;
                }
                return undefined;
              })()}
              entityType={entityType!}
              entityId={entityId!}
              entityName={entityName}
              fieldConfig={config?.fieldsPermissions || {}}
              fieldLabels={config?.fieldLabels || {}}
              kbId={selectedKbId || kbIds?.[0] || undefined}
              kbName={kbName}
              projectName={entityName}
              documentIds={computedDocumentIds}
              language={i18n.language as 'en' | 'fr'}
              activeTab={activeTab}
              activeField={activeField}
              onFieldUpdate={handleAIFieldUpdate}
              getFieldValue={getFieldValue}
              onContextFieldChanged={handleContextFieldChanged}
              onExtractionResults={handleExtractionResults}
              showMetrics={showMetrics}
              solutionId={activeSolutionId ?? undefined}
              initialMessage={resumeBriefing}
              proactiveInit={entityType === 'project'}
            />
          )}
        </div>

        {/* Editor Pane (Right) */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <RightPaneContentSwitcher
            entityType={entityType!}
            entityId={entityId!}
            entityName={entityName}
            entity={entity}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            documentTabs={documents}
            onCloseDocumentTab={closeDocumentTab}
            fieldConfig={config}
            activeField={activeField}
            expandedFields={expandedFields}
            highlightedFields={highlightedFields}
            onFieldChange={handleFieldChange}
            onFieldFocus={handleFieldFocus}
            onFieldBlur={handleFieldBlur}
            onToggleExpand={toggleFieldExpand}
            searchSessionsData={searchSessionsData}
            kbsData={kbsData}
            selectedKbId={selectedKbId}
            onSelectKb={handleKbSelect}
            onSelectSearch={handleSelectSearch}
            kbName={kbName}
            kbDocumentsTabRef={kbDocumentsTabRef}
            onKbSelectionChange={handleKbSelectionChange}
            kbDocNumberMap={kbDocNumberMap}
            onKbDocumentsDeleted={(ids) => {
              if (!ids.length) return;
              setSelectedKbDocumentIds([]);
              setKbsData(prev => prev.map(kb => {
                if (kb.id !== entityId) return kb;
                return { ...kb, document_count: Math.max(0, kb.document_count - ids.length) };
              }));
              setEntity(prev => {
                if (!prev) return prev;
                const currentCount = Number((prev as unknown as Record<string, unknown>).document_count || 0);
                return { ...prev, document_count: Math.max(0, currentCount - ids.length) } as unknown as Entity;
              });
            }}
            onKbSearchTabCreated={entityType === 'kb' ? (transcriptId: string, label: string) => {
              if (!searchResultsTabs.find(t => t.tabId === transcriptId)) {
                setSearchResultsTabs(prev => [...prev, {
                  tabId: transcriptId,
                  tabName: label,
                  kbId: entityId ?? '',
                  kbName: kbName || '',
                  searchType: 'extraction',
                  documents: [],
                  timestamp: new Date().toISOString(),
                  isSaved: false,
                }]);
              }
              setActiveTab(transcriptId);
            } : undefined}
            onKbSearchResultsReady={entityType === 'kb' ? (transcriptId: string, documents) => {
              setSearchResultsTabs(prev => prev.map(t =>
                t.tabId === transcriptId
                  ? { ...t, documents: documents.map(d => ({
                      document_id: d.document_id,
                      document_title: d.document_title,
                      relevance_score: d.relevance_score,
                      justification: d.justification,
                      relevance_explanation: d.relevance_explanation,
                    })) }
                  : t
              ));
              setActiveTab(transcriptId);
            } : undefined}
            onKbSearchError={entityType === 'kb' ? (_transcriptId: string, message: string) => {
              addToast({ type: 'error', title: t('kbSearch.errorTitle', 'Search failed'), message });
            } : undefined}
            searchResultsTabs={searchResultsTabs}
            searchTabSelections={searchTabSelections}
            onSearchSelectionChange={handleSearchTabSelectionChange}
            onSaveSearchResults={handleSaveSearchResults}
            onSaveSearch={handleSaveSearch}
            onSearchDocumentClick={handleSearchDocumentClick}
            contextKbsData={contextKbsData}
            onOpenContextKbTab={handleOpenContextKbTab}
            onCreateContextKb={handleCreateContextKb}
            canCreateContextKb={canEditProjects}
            onOpenWorkspace={entityType === 'sub-project' ? (contextField: string) => {
              const params = new URLSearchParams();
              if (activeSolutionId) params.set('solutionId', activeSolutionId);
              navigate(`/workspace/${entityId}/${contextField}${params.size ? '?' + params.toString() : ''}`);
            } : undefined}
            onFufAccept={async (fieldName, value) => {
              if (!entityId || !config) return;
              handleFieldChange(fieldName, value);
              if (fieldName === 'state_of_the_art') {
                const currentSota = entity ? (entity as unknown as Record<string, { version?: number }>).state_of_the_art : null;
                try {
                  await config.service.update(entityId, {
                    state_of_the_art: { content: value, version: currentSota?.version ?? 1, generation_status: 'enriched' },
                  });
                } catch (err) {
                  console.error('Failed to save enriched state_of_the_art:', err);
                }
              } else {
                saveField(fieldName, value);
              }
            }}
            customFieldRenderers={entityType === 'sub-project' ? {
              project_features: (
                <div className={`metadata-field-row ${expandedFields['project_features'] ? 'expanded' : 'collapsed'}`}>
                  <div className="metadata-field-header" style={{ cursor: 'pointer' }} onClick={() => toggleFieldExpand('project_features')}>
                    <span className="expand-toggle">{expandedFields['project_features'] ? '▼' : '▶'}</span>
                    <span className="field-label">{t('scv2.fields.project_features', 'Product Features')}</span>
                    <span className="readonly-badge">Read-only</span>
                  </div>
                  {expandedFields['project_features'] && (
                    <div className="metadata-field-body">
                      <div className="metadata-field-readonly" style={{ whiteSpace: 'pre-wrap' }}>
                        {parentProject?.project_features || '—'}
                      </div>
                    </div>
                  )}
                </div>
              ),
              solutions: (
                <>
                  <SolutionProgressMatrix
                    solutions={solutions}
                    activeSolutionId={activeSolutionId}
                    onSolutionClick={handleSolutionChange}
                  />
                  <SolutionsBox
                    solutions={solutions}
                    activeSolutionId={activeSolutionId}
                    onSolutionChange={handleSolutionChange}
                    onAddSolution={handleAddSolution}
                    canEdit={canEditProjects}
                    activeField={activeField}
                    onFieldFocus={handleFieldFocus}
                    onFieldChange={handleSolutionFieldChange}
                    expandedFields={expandedFields}
                    onToggleExpand={toggleFieldExpand}
                    highlightedFields={highlightedFields}
                    subProjectId={entityId}
                    kbId={selectedKbId}
                  />
                </>
              ),
            } : {}}
          />
        </div>
      </div>

      <SCV2Modals
        entityType={entityType}
        entityId={entityId}
        entityName={entityName}
        entityProjectId={(entity as SubProject)?.project_id}
        config={config}
        canCreateSubprojects={canCreateSubprojects}
        showDeleteModal={showDeleteModal}
        onCloseDeleteModal={() => setShowDeleteModal(false)}
        onConfirmDelete={handleDeleteEntity}
        showSubProjectModal={showSubProjectModal}
        onCloseSubProjectModal={() => setShowSubProjectModal(false)}
        subProjectFormData={subProjectFormData}
        setSubProjectFormData={setSubProjectFormData}
        onSubmitCreateSubProject={handleCreateSubProject}
        showViewSubProjectsModal={showViewSubProjectsModal}
        onCloseViewSubProjectsModal={() => setShowViewSubProjectsModal(false)}
        onOpenSubProjectModal={() => { setShowViewSubProjectsModal(false); setShowSubProjectModal(true); }}
        subProjectsList={subProjectsList}
        loadingSubProjects={loadingSubProjects}
        onSubProjectCardClick={handleSubProjectCardClick}
        showSearchServiceModal={showSearchServiceModal}
        onCloseSearchServiceModal={() => setShowSearchServiceModal(false)}
        onSelectSearchService={handleSearchServiceSelect}
        showAddDocModal={showAddDocModal}
        onCloseAddDocModal={() => setShowAddDocModal(false)}
        uploadMode={uploadMode}
        setUploadMode={setUploadMode}
        selectedFiles={selectedFiles}
        setSelectedFiles={setSelectedFiles}
        pageUrl={pageUrl}
        setPageUrl={setPageUrl}
        uploading={uploading}
        onSubmitUpload={handleUploadDocuments}
        showContextKbModal={showContextKbModal}
        onCloseContextKbModal={() => { setShowContextKbModal(false); setContextKbConfig(null); }}
        contextKbConfig={contextKbConfig}
        onContextKbCreated={handleContextKbCreated}
        showTechWatchConfig={showTechWatchConfig}
        onCloseTechWatchConfig={() => setShowTechWatchConfig(false)}
        onTechWatchSaved={(w) => { const wTyped = w as { is_active?: boolean; id?: string }; setTechWatchStatus({ exists: true, is_active: wTyped.is_active, watch_id: wTyped.id }); setShowTechWatchConfig(false); setShowTechWatchDigest(true); }}
        onTechWatchDeleted={() => { setTechWatchStatus({ exists: false }); setShowTechWatchDigest(false); }}
        showTechWatchDigest={showTechWatchDigest}
        onCloseTechWatchDigest={() => setShowTechWatchDigest(false)}
        onEditWatch={() => { setShowTechWatchDigest(false); setShowTechWatchConfig(true); }}
        techWatchStatus={techWatchStatus}
      />

      {entityType === 'sub-project' && entityId && showOpsRetrievalModal && (
        <OpsRetrievalModal
          subProjectId={entityId}
          onClose={() => setShowOpsRetrievalModal(false)}
        />
      )}

      {entityType === 'sub-project' && entityId && showOpenalexRetrievalModal && (
        <OpenalexRetrievalModal
          subProjectId={entityId}
          onClose={() => setShowOpenalexRetrievalModal(false)}
        />
      )}

      {entityType === 'sub-project' && entityId && (
        <ExtractionLaunchModal
          isOpen={showExtractionModal}
          subProjectId={entityId}
          forcedRegime={extractionRegime}
          onClose={() => setShowExtractionModal(false)}
          onLaunched={(transcriptId) => {
            setShowExtractionModal(false);
            window.open(`/search-transcript/${transcriptId}`, '_blank');
          }}
        />
      )}

      {entityType === 'kb' && (
        <RAGPrepareCostDialog
          isOpen={showRagCostDialog}
          kbId={entityId}
          onClose={() => setShowRagCostDialog(false)}
          onConfirm={executePrepareRag}
        />
      )}
    </div>
  );
};

export default SpeakingChatbotV2;

